/**
 * Transformation des lignes brutes d'un widget en données de Gantt amCharts 5.
 *
 * Le module `gantt` d'amCharts (type de graphe de premier rang depuis la 5.14.0) attend DEUX jeux
 * de données distincts, reliés par `id` :
 *  - `gantt.yAxis.data`   → les lignes de la colonne de gauche : `{ id, name, parentId? }`
 *  - `gantt.series.data`  → les barres : `{ id, start, end | duration, progress?, linkTo? }`
 *
 * Tout est pur ici, pour que la partie fragile — l'interprétation des dates — soit testable sans
 * monter un graphe.
 */

export interface GanttColumnRoles {
  /** `valueId` de la colonne portant le libellé de la ligne. */
  label?: string
  start?: string
  end?: string
  progress?: string
  /** `valueId` de la colonne de regroupement : chaque valeur devient une ligne parente. */
  parent?: string
}

export interface GanttCategory {
  id: string
  name: string
  parentId?: string
}

export interface GanttTask {
  id: string
  /** idSample du sample d'origine — transmis au drill-down pour filtrer sur cet élément. */
  idSample?: string
  start: number
  /**
   * Conservé pour le cadrage initial de l'axe (`getGanttTimeRange`) — mais ce n'est PAS ce qui
   * dessine la barre : voir `duration`.
   */
  end?: number
  /**
   * ⚠️ C'EST `duration` QUI DESSINE LA BARRE, PAS `end`.
   *
   * `GanttSeries._afterDataChange()` appelle `_fixValues(dataItem, "duration")` sur chaque point de
   * données, et cette branche fait `valueX = openValueX + duration * getUnitDuration()`. Un `end`
   * fourni est donc ÉCRASÉ, et une tâche sans `duration` reçoit `duration = 0` par défaut : la barre
   * a une longueur nulle et s'affiche en losange. C'est ce qui s'est produit à l'écran le
   * 2026-08-19 avec des `start`/`end` parfaitement corrects — et c'est pourquoi tous les exemples
   * livrés par amCharts passent `start` + `duration`, jamais `end`.
   *
   * Exprimé dans l'unité de `durationUnit` du graphe.
   */
  duration?: number
  progress: number
}

export interface GanttBuildResult {
  categories: GanttCategory[]
  tasks: GanttTask[]
  /** Lignes écartées faute de libellé ou de date de début exploitable. Jamais silencieux. */
  skippedRows: number
  /**
   * La première valeur que `toTimestamp` n'a pas su lire, telle quelle. Un compteur muet est un
   * mauvais message d'erreur : sans l'échantillon, on soupçonne la conversion de dates alors que
   * le problème est souvent ailleurs (colonne vide, rôle non attribué, valeur inattendue).
   */
  unreadableSample?: string
  /**
   * Lignes dont la date de FIN précède la date de DÉBUT. Rendues en jalon — mais comptées, parce
   * que le symptôme (un graphe entier de losanges) est indiscernable d'un problème de conversion de
   * dates. Vécu le 2026-08-19 : deux rôles inversés ont coûté un quart d'heure de recherche du
   * mauvais côté. La cause est presque toujours l'une des deux : rôles début/fin inversés, ou
   * données incohérentes.
   */
  reversedRows: number
  /**
   * `true` quand la MÊME colonne est attribuée au début et à la fin. Toutes les barres ont alors une
   * longueur nulle et s'affichent en losange — ce qui ressemble à un défaut du widget, pas à un
   * réglage. Détecté le 2026-08-19 après vingt minutes passées à soupçonner la conversion des dates,
   * alors que la sonde a montré `start === end` en une ligne.
   */
  sameColumnForStartAndEnd: boolean
  /**
   * Lignes dont la fin ÉGALE le début. Une barre de longueur nulle est légitime (un jalon daté),
   * mais quand elles le sont toutes, c'est un réglage à revoir : on le compte pour pouvoir le dire.
   */
  zeroLengthRows: number
  /**
   * Lignes dont l'étendue est NON NULLE mais plus courte que l'unité de durée choisie : la durée
   * arrondit à 0, donc la barre se dessine en losange. Le geste correctif est de passer l'unité à
   * une granularité plus fine (heure), pas de toucher aux données — d'où un compteur distinct.
   */
  subUnitRows: number
}

const MS_PER_DAY = 24 * 60 * 60 * 1000

/**
 * Seuils de discrimination d'une date numérique. Les valeurs de ce produit arrivent en
 * millisecondes, en secondes ou en **nanosecondes** selon le chemin (cf. `$numberLong` de Mongo et
 * `getDateDDMMYYYYFromNanoSec` dans `utils/dateUtils.js`). Se tromper d'unité ne produit pas une
 * erreur : ça place la tâche en 1970 ou en l'an 56 000. D'où des bornes explicites et testées.
 *
 * Ordres de grandeur pour une date des années 2020 : ~1,7e9 s / ~1,7e12 ms / ~1,7e18 ns.
 */
const NANOSECONDS_FLOOR = 1e15
const MILLISECONDS_FLOOR = 1e11
const SECONDS_FLOOR = 1e8

const DDMMYYYY = /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:[ T](\d{1,2}):(\d{2}))?$/

/**
 * ⚠️ `Date.parse` ne rend PAS `NaN` sur du texte libre : il y pêche ce qui ressemble à une date.
 * Mesuré le 2026-08-19 — `Date.parse("janv. 2026")` rend le 1er janvier 2026. Or « janv. 2026 »
 * est exactement la forme qu'un libellé d'affichage prend dans ce produit. Sans filtre, une
 * colonne non datée placerait donc des tâches au 1er janvier au lieu d'être signalée. D'où un
 * format ISO exigé explicitement avant tout appel à `Date.parse`.
 */
const ISO_LIKE = /^\d{4}-\d{2}-\d{2}(?:[T ]\d{2}:\d{2}(?::\d{2})?(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})?)?$/

const fromNumber = (value: number): number | undefined => {
  if (!Number.isFinite(value) || value <= 0) return undefined
  if (value >= NANOSECONDS_FLOOR) return Math.round(value / 1e6)
  if (value >= MILLISECONDS_FLOOR) return Math.round(value)
  if (value >= SECONDS_FLOOR) return Math.round(value * 1000)
  return undefined
}

/**
 * Horodatage en millisecondes, ou `undefined` si la valeur n'est pas une date exploitable.
 *
 * ⚠️ Une colonne de date passée en PREMIER `groupBy` est reformatée en libellé d'affichage par
 * `getTransformedData` (« janv. 2026 ») avant d'atteindre le widget : elle n'est donc pas
 * analysable ici, et la ligne sera comptée dans `skippedRows` plutôt que placée n'importe où.
 */
export const toTimestamp = (value: unknown): number | undefined => {
  if (value === null || value === undefined || value === "") return undefined

  if (value instanceof Date) {
    const time = value.getTime()
    return Number.isFinite(time) ? time : undefined
  }

  if (typeof value === "number") return fromNumber(value)

  if (typeof value === "object") {
    const long = (value as { $numberLong?: unknown }).$numberLong
    if (typeof long === "string" || typeof long === "number") return fromNumber(Number(long))
    return undefined
  }

  if (typeof value !== "string") return undefined

  const trimmed = value.trim()
  if (!trimmed) return undefined

  const french = DDMMYYYY.exec(trimmed)
  if (french) {
    const [, day, month, year, hours, minutes] = french
    const parsed = new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hours ?? 0),
      Number(minutes ?? 0)
    )
    // `new Date(2026, 12, 40)` ne lève pas : il déborde sur le mois suivant. On refuse donc une
    // date dont les composants ne se retrouvent pas à l'identique après construction.
    if (
      parsed.getFullYear() !== Number(year) ||
      parsed.getMonth() !== Number(month) - 1 ||
      parsed.getDate() !== Number(day)
    ) {
      return undefined
    }
    return parsed.getTime()
  }

  // Une chaîne purement numérique est une date numérique déguisée, pas une année ISO.
  if (/^\d+$/.test(trimmed)) return fromNumber(Number(trimmed))

  if (!ISO_LIKE.test(trimmed)) return undefined

  const parsed = Date.parse(trimmed)
  return Number.isNaN(parsed) ? undefined : parsed
}

/**
 * Avancement ramené dans [0, 1].
 *
 * Les deux conventions coexistent dans les référentiels : un taux (0,42) et un pourcentage (42).
 * Une valeur strictement supérieure à 1 est donc lue comme un pourcentage — sans quoi toute barre
 * exprimée en pourcentage apparaîtrait à 100 % d'avancement.
 */
export const toProgress = (value: unknown): number => {
  const raw = typeof value === "string" ? Number(value.replace(",", ".").replace("%", "")) : value
  if (typeof raw !== "number" || !Number.isFinite(raw) || raw <= 0) return 0
  const ratio = raw > 1 ? raw / 100 : raw
  return Math.min(1, ratio)
}

const readCell = (row: Record<string, unknown>, valueId?: string): unknown =>
  valueId ? row?.[valueId] : undefined

/** Suffixe de la clé jumelle que le pipeline ajoute aux colonnes de date. */
const RAW_SUFFIX = "_raw"

/**
 * Valeur d'une colonne de DATE.
 *
 * Le pipeline renvoie DEUX clés par colonne de date — mesuré sur la réponse de l'API le
 * 2026-08-19 :
 *   "<valueId>"      : "22/08/2026"    ← la chaîne d'AFFICHAGE
 *   "<valueId>_raw"  : 1787356800000   ← la valeur, en millisecondes
 *
 * On lit donc `_raw` D'ABORD. Passer par la chaîne d'affichage serait un aller-retour par le
 * formatage : elle dépend de la locale du compte, et le jour où elle rend « 08/22/2026 » ou
 * « Aug 22, 2026 », l'analyse par forme se tromperait de mois ou échouerait — sans rien signaler.
 * Le repli sur la chaîne reste utile : toutes les colonnes ne portent pas de jumelle `_raw`.
 */
const readDateCell = (row: Record<string, unknown>, valueId?: string): unknown => {
  if (!valueId) return undefined
  const raw = row?.[valueId + RAW_SUFFIX]
  if (raw !== undefined && raw !== null && raw !== "") return raw
  return row?.[valueId]
}

const readText = (row: Record<string, unknown>, valueId?: string): string => {
  const cell = readCell(row, valueId)
  if (cell === null || cell === undefined) return ""
  return String(cell).trim()
}

/**
 * Construit les deux jeux de données du Gantt à partir des lignes brutes du widget.
 *
 * Une ligne est écartée si elle n'a pas de libellé ou pas de date de début exploitable — les deux
 * seules informations sans lesquelles une barre n'a nulle part où aller. Le compte est rendu dans
 * `skippedRows` pour que l'écran puisse le dire : une ligne disparue en silence ferait conclure à
 * un filtre mal réglé.
 *
 * Une ligne sans date de fin devient un **jalon** (`duration: 0`), qu'amCharts rend en losange.
 */
export const buildGanttData = (
  rows: Array<Record<string, unknown>> | undefined,
  roles: GanttColumnRoles | undefined,
  /**
   * Millisecondes par unité de durée du graphe — à obtenir de `am5.time.getDuration(durationUnit)`,
   * la fonction dont amCharts se sert lui-même, pour qu'aucune divergence d'arrondi ne soit
   * possible. Défaut : un jour.
   */
  unitMs: number = 24 * 60 * 60 * 1000
): GanttBuildResult => {
  const empty: GanttBuildResult = {
    categories: [],
    tasks: [],
    skippedRows: 0,
    reversedRows: 0,
    sameColumnForStartAndEnd: false,
    zeroLengthRows: 0,
    subUnitRows: 0,
  }
  if (!Array.isArray(rows) || rows.length === 0) return empty
  if (!roles?.label || !roles?.start) return empty

  const categories: GanttCategory[] = []
  const tasks: GanttTask[] = []
  const parentIdByName = new Map<string, string>()
  /**
   * Étendue des enfants de chaque ligne parente.
   *
   * ⚠️ amCharts attend UNE tâche par catégorie. Une ligne parente sans tâche produit un artefact à
   * l'écran — mesuré le 2026-08-19 : 3 lignes pour 2 tâches, et la ligne sans tâche se dessinait en
   * losange. Chaque parent reçoit donc une barre de SYNTHÈSE couvrant ses enfants, ce qui est aussi
   * la sémantique Gantt attendue d'une ligne de regroupement.
   */
  const childrenSpanByParent = new Map<string, { start: number; end: number }>()
  let skippedRows = 0
  let reversedRows = 0
  let zeroLengthRows = 0
  let subUnitRows = 0
  let unreadableSample: string | undefined

  /** Retient le premier échantillon illisible, pour que l'écran puisse le montrer. */
  const noteUnreadable = (cell: unknown) => {
    if (unreadableSample !== undefined) return
    if (cell === undefined || cell === null || cell === "") return
    unreadableSample = String(cell)
  }

  rows.forEach((row, index) => {
    const name = readText(row, roles.label)
    const startCell = readDateCell(row, roles.start)
    const start = toTimestamp(startCell)

    if (!name || start === undefined) {
      skippedRows += 1
      if (name && start === undefined) noteUnreadable(startCell)
      return
    }

    let parentId: string | undefined
    const parentName = readText(row, roles.parent)
    if (parentName) {
      if (!parentIdByName.has(parentName)) {
        const generated = `gantt_group_${parentIdByName.size}`
        parentIdByName.set(parentName, generated)
        categories.push({ id: generated, name: parentName })
      }
      parentId = parentIdByName.get(parentName)
    }

    const id = `gantt_${index}`
    categories.push({ id, name, ...(parentId ? { parentId } : {}) })

    const endCell = readDateCell(row, roles.end)
    const end = toTimestamp(endCell)
    if (end === undefined) noteUnreadable(endCell)
    // idSample : l'ETL inclut soit "idSample" (Gantt direct) soit "loaded_left_xxx.idSample"
    // (Gantt lié). On cherche la première clé se terminant par "idSample" dans la row.
    // Repli sur `id` si aucune clé idSample n'est présente (config non migrée).
    const idSampleKey = Object.keys(row).find(k => k === "idSample" || k.endsWith(".idSample"))
    const task: GanttTask = {
      id,
      idSample: idSampleKey != null && row[idSampleKey] != null ? String(row[idSampleKey]) : id,
      start,
      progress: toProgress(readCell(row, roles.progress)),
    }

    if (end === undefined) {
      // Pas de fin exploitable : jalon. Mieux qu'une barre d'une durée inventée.
      task.duration = 0
    } else if (end === start) {
      // Fin égale au début : barre de longueur nulle, donc losange. Légitime pour un jalon daté,
      // suspect quand toutes les lignes le sont.
      task.duration = 0
      zeroLengthRows += 1
    } else if (end > start) {
      // Le cas normal. On donne les DEUX : `duration` dessine la barre, `end` sert au cadrage.
      task.end = end
      task.duration = Math.round((end - start) / unitMs)
      if (task.duration === 0) subUnitRows += 1
    } else if (end < start) {
      // Fin antérieure au début. On garde la ligne — l'écarter la ferait disparaître — mais en
      // jalon, à sa date de début, ce qui reste vrai. ⚠️ Et on la COMPTE : « ça se remarque » était
      // faux, un graphe entier de losanges ressemble à un défaut du widget et non à un réglage
      // inversé.
      task.duration = 0
      reversedRows += 1
    }

    tasks.push(task)

    if (parentId) {
      const childEnd = task.end !== undefined ? task.end : start
      const span = childrenSpanByParent.get(parentId)
      childrenSpanByParent.set(
        parentId,
        span
          ? { start: Math.min(span.start, start), end: Math.max(span.end, childEnd) }
          : { start, end: childEnd }
      )
    }
  })

  // Une barre de synthèse par ligne parente, APRÈS la boucle : son étendue n'est connue qu'une fois
  // tous ses enfants lus.
  childrenSpanByParent.forEach((span, parentId) => {
    const spans = span.end > span.start
    tasks.push({
      id: parentId,
      start: span.start,
      progress: 0,
      // Même règle que pour les enfants : c'est `duration` qui dessine.
      ...(spans
        ? { end: span.end, duration: Math.round((span.end - span.start) / unitMs) }
        : { duration: 0 }),
    })
  })

  return {
    categories,
    tasks,
    skippedRows,
    reversedRows,
    zeroLengthRows,
    subUnitRows,
    sameColumnForStartAndEnd: Boolean(roles.end) && roles.start === roles.end,
    unreadableSample,
  }
}

/** Étendue temporelle des tâches, pour un cadrage initial lisible. */
export const getGanttTimeRange = (
  tasks: GanttTask[]
): { start: number; end: number } | undefined => {
  if (!tasks.length) return undefined
  let start = Infinity
  let end = -Infinity
  tasks.forEach(task => {
    if (task.start < start) start = task.start
    const taskEnd = task.end ?? task.start
    if (taskEnd > end) end = taskEnd
  })
  if (!Number.isFinite(start) || !Number.isFinite(end)) return undefined
  // Une seule date, ou toutes identiques : on ouvre une fenêtre d'un jour pour que l'axe existe.
  if (end === start) return { start, end: start + MS_PER_DAY }
  return { start, end }
}
